import { useAuthContext } from "../../auth/state/AuthContext";
import { TimeCharter } from "../../search-vessels/models/search.interfaces";
import { parseSortAndFilterTcs } from "../../search-vessels/services/search-vessels.service";
import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import logo from "../../shared/assets/Vector.svg";
import { selectedVesselAtom, themeAtom } from "../../shared/atoms";
import Checkbox from "../../shared/components/Checkbox";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { toastError, toastInfo } from "../../shared/components/ToastContainer";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import { MyVesselsTable } from "../components/MyVesselsTable";
import { VesselDetails } from "../models/home.interfaces";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

const Home: React.FC = () => {
  const { firstname, lastname } = useAuthContext().state.user?.accessToken || {};
  const fullName = firstname && lastname ? `${firstname} ${lastname}` : undefined;
  const setSelectedVessel = useSetAtom(selectedVesselAtom);
  const [vesselsDetails, setVesselsDetails] = useState<VesselDetails[] | null>(null);
  const [timeCharters, setTimeCharters] = useState<TimeCharter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showInactiveVessels, setshowInactiveVessels] = useState(false);
  const [showcompletedVoyages, setshowcompletedVoyages] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const theme = useAtomValue(themeAtom);

  useEffect(() => {
    const fetchData = async () => {
      if (!fullName) {
        return;
      }
      try {
        setIsLoading(true);

        const result = await api.get(`${apiUrl}/userAssignedVessel`, {
          params: {
            ops_coordinator: fullName,
            showInactiveVessels: showInactiveVessels.toString(),
            showcompletedVoyages: showcompletedVoyages.toString(),
            includeCount: "false",
          },
        });
        log("STATE - fetchData - my vessels", { count: result.data.data?.length ?? 0 });
        setVesselsDetails(result.data.data);
      } catch (error: unknown) {
        logE(new Error("Home - fetchData - failed"));
        setVesselsDetails([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [fullName, showInactiveVessels, showcompletedVoyages]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    if (date.getFullYear() === 1970) {
      return "N/A";
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const removeUser = async (event: React.MouseEvent<HTMLSpanElement>, index: number, item: VesselDetails) => {
    event.stopPropagation();
    log("Home - removeUser", { vesselCode: item.VESSEL_CODE });

    try {
      const requestData = {
        vessel_code: item.VESSEL_CODE,
        vessel_name: item.VESSEL_NAME,
        imo_number: item.IMO_NUMBER,
        user_name: fullName,
      };

      setIsLoading(true);
      const result = await api.post(`${apiUrl}/userdelVessel`, undefined, {
        params: requestData,
      });

      if (result.data.success === true) {
        log("STATE - removeUser - removed");
        toastInfo("Removed from My vessels");
        setVesselsDetails(vesselsDetails?.filter((_, i) => i !== index) ?? null);
      }
    } catch (error: unknown) {
      logE(new Error("Home - removeUser - failed"));
      toastError("Error removing vessel");
    } finally {
      setIsLoading(false);
    }
  };

  const tcCodeGetAPI = async (item: VesselDetails) => {
    setIsLoading(true);
    setTimeCharters([]);
    try {
      const { tcCodes } = parseSortAndFilterTcs(item.TCCODE_TCSTATUS_CPDELDT_CPREDELDT_VOYAGENO);
      const result = await api.get(`${apiUrl}/tccodevoyagelist`, {
        params: {
          vessel_name: item.VESSEL_NAME,
          // TODO: Update Backend API to accept array of tc_codes in the conventional way
          tc_code: String([tcCodes]),
        },
      });

      const output = result.data;

      // Sort voyages by custom order of TC_STATUS
      const sortedVoyages = output
        .sort((a: TimeCharter, b: TimeCharter) => {
          // Sort by LAST_UPDATED_GMT, putting null or empty dates at the end
          const getDateValue = (item: TimeCharter) => {
            const dateValue = item.CP_DATE?.trim();
            return dateValue ? new Date(dateValue).getTime() : 0;
          };

          // Prioritize by LAST_UPDATED_GMT in descending order
          return getDateValue(b) - getDateValue(a);
        })
        .sort((a: TimeCharter, b: TimeCharter) => {
          // Define the custom order of TC_STATUS
          const order = ["Scheduled", "Commenced", "Completed", "Closed"];

          // Get the index of TC_STATUS in the custom order array
          const indexA = order.indexOf(a.TC_STATUS);
          const indexB = order.indexOf(b.TC_STATUS);

          // Compare the indexes to determine the order
          return indexA - indexB;
        });

      log("STATE - tcCodeGetAPI - apply", { count: sortedVoyages.length });
      setTimeCharters(sortedVoyages);
    } catch (error: unknown) {
      logE(new Error("Home - tcCodeGetAPI - failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAccordion = async (index: number, item: VesselDetails) => {
    setSelectedVessel(item);
    setOpenIndex(index === openIndex ? null : index);
    if (index !== openIndex) {
      await tcCodeGetAPI(item);
    }
  };

  const toggleShowInactiveVessels = async () => {
    setshowInactiveVessels(!showInactiveVessels);
    setOpenIndex(null);
  };
  const toggleShowcompletedVoyages = async () => {
    log("Home - toggleShowcompletedVoyages", { enabled: !showcompletedVoyages });
    setshowcompletedVoyages(!showcompletedVoyages);
    setOpenIndex(null);
  };

  logV("Home - BUILD", { loading: isLoading, count: vesselsDetails?.length ?? 0 });

  return (
    <LoadingWrapper
      isLoading={isLoading}
      loadingIndicatorClassName={theme.home.tailwind.spinner}
      loadingIndicatorSize="lg"
    >
      <div className="flex h-screen">
        <Sidebar />
        <div className={twMerge("flex flex-1 flex-col overflow-y-hidden", theme.home.tailwind.pageBackground)}>
          <Header />
          <div className="flex flex-col gap-12 px-6">
            <h1 className={twMerge("text-4xl font-bold capitalize", theme.home.tailwind.textDefault)}>
              Welcome {firstname}
            </h1>
            <div
              className={twMerge(
                "flex h-[80vh] flex-col gap-5 rounded-lg p-6 shadow-md",
                theme.home.tailwind.panelBackground,
                !vesselsDetails || !Array.isArray(vesselsDetails) || vesselsDetails.length === 0
                  ? "overflow-hidden"
                  : "overflow-auto",
              )}
            >
              <div className="flex justify-start gap-4">
                <img alt="" src={logo} />
                <div className={twMerge("text-2xl font-bold", theme.home.tailwind.textDefault)}>My vessels</div>
                <div className="flex-1"></div>
                <Checkbox
                  label="Include vessels with completed voyages"
                  checked={showcompletedVoyages}
                  onChange={toggleShowcompletedVoyages}
                />
                <Checkbox
                  label="Include vessels with Inactive TCs"
                  checked={showInactiveVessels}
                  onChange={toggleShowInactiveVessels}
                />
              </div>
              <MyVesselsTable
                data={vesselsDetails}
                formatDate={formatDate}
                removeUser={removeUser}
                timeCharters={timeCharters}
                openIndex={openIndex}
                toggleAccordion={toggleAccordion}
              />
            </div>
          </div>
          <PageFooter />
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default Home;
